import type { HealthColor, HealthConfig } from '@ofmos/shared/health-config';
import type { FieldMetadata, FieldValue } from '../../data/client';
import { TagDot } from '../../fields/display/TagDot';
import { formatRelative } from '../../fields/format';
import type { TagTone } from '../../ui';
import { todayKey } from './dueDate';

/**
 * THE COMPUTED HEALTH, AS ONE ANSWER (`phase-b-coach.md` D17, and D2's note).
 *
 * FIVE FIELDS, ONE BLOCK. `health`, `health_reason`, `health_since`, `health_computed_at`
 * and `needs_assessment` are five rows in the data model and ONE sentence in a coach's
 * head - "red, since four days, because the client has not written". Rendering them as five
 * labelled rows would make a reader assemble that sentence themselves, every time, on a
 * screen whose whole job is to have already done it.
 *
 * IT IS READ-ONLY AND SAYS WHY, WITHOUT SOUNDING LIKE A REFUSAL. D2 refuses a hand write at
 * the record layer, and a person who meets that refusal by clicking has learned nothing
 * except that the product said no. So the block never offers the click and the note points
 * at what they CAN do: "Computed by the system. Give your judgment below."
 *
 * `needs_assessment` IS NOT RENDERED AS A ROW. It is a boolean the panel filters on, and
 * "Needs an assessment: yes" beside a note that already says "give your judgment below" is
 * the same instruction twice, one of them in schema voice.
 */

export interface HealthBlockProps {
  readonly config: HealthConfig;
  readonly values: Readonly<Record<string, FieldValue>>;
}

/**
 * WAVE 3 (R-B1): THE COLOUR IS THE PRODUCT'S DOT, NOT A CAPSULE.
 *
 * This was the last capsule on the record and round 3's own record named it: the block reads
 * the health CONFIG rather than a field display, so the owner's round-2 decision - a stored
 * option is text with a leading colour dot - had never reached it, and a coach met the same
 * fact in two shapes on one screen. `TagDot` is the one implementation (P5-17), and its tones
 * are the product's semantic colour roles, so red, yellow and green keep their meaning.
 */
const TONES: Record<HealthColor, TagTone> = {
  green: 'success',
  yellow: 'warning',
  red: 'danger',
};

const LABELS: Record<HealthColor, string> = { green: 'Green', yellow: 'Yellow', red: 'Red' };

export function HealthBlock({ config, values }: HealthBlockProps) {
  const colour = colourOf(config, values[config.colorField]);
  const reasons = reasonLines(values[config.reasonField]);
  const since = sinceLabel(values[config.sinceField]);
  const computedAt = values[config.computedAtField];

  return (
    /**
     * A NAMED REGION, because the block is one answer and a screen reader should be able to
     * jump to it rather than walking three unlabelled paragraphs. It is also what lets a
     * test scope an assertion to the capsule rather than to every "Yellow" on the page.
     */
    <section aria-label="Computed health" className="space-y-2 px-(--record-row-inset) py-3">
      <p className="flex flex-wrap items-baseline gap-2">
        {colour === null ? (
          /**
           * NO COLOUR IS A STATE, NOT A BLANK. It happens when every rule the config carries
           * is behind a stale guard that has fired, and the reason lines below say which
           * signal is missing. The neutral dot keeps the block's shape and refuses to
           * claim green.
           */
          <span className="list-value text-sm text-ink">
            <TagDot tone="neutral" />
            <span>No score yet</span>
          </span>
        ) : (
          <span className="list-value text-sm font-medium text-ink">
            <TagDot tone={TONES[colour]} />
            <span>{LABELS[colour]}</span>
          </span>
        )}
        {since !== null && (
          /** Tabular, so the number does not shift when the block re-renders a day later. */
          <span className="text-2xs text-ink-muted tabular-nums">{since}</span>
        )}
      </p>

      {reasons.length > 0 && (
        <ul className="space-y-0.5">
          {reasons.map((reason) => (
            <li key={reason} className="text-2xs text-ink-muted">
              {reason}
            </li>
          ))}
        </ul>
      )}

      <p className="text-2xs text-ink-subtle">
        {typeof computedAt === 'string' ? `Computed ${formatRelative(computedAt)}. ` : ''}
        Computed by the system. Give your judgment below.
      </p>
    </section>
  );
}

/** The option id the config says means each colour. Nothing here reads a label. */
function colourOf(config: HealthConfig, value: FieldValue | undefined): HealthColor | null {
  if (typeof value !== 'string') return null;
  if (value === config.colorOptions.red) return 'red';
  if (value === config.colorOptions.yellow) return 'yellow';
  if (value === config.colorOptions.green) return 'green';
  return null;
}

/**
 * ONE LINE PER FINDING, in the order the engine wrote them - worst first. The field is a
 * multiline TEXT and the newlines are the structure, so splitting on them here is reading
 * the value rather than parsing it.
 */
function reasonLines(value: FieldValue | undefined): readonly string[] {
  if (typeof value !== 'string') return [];
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line !== '');
}

/**
 * "since 4 days", in CALENDAR DAYS from a DATE - never from an instant.
 *
 * EXPORTED, because the attention lane says the same thing about the same field and the two
 * must not drift: `ageInDays` next door renders "12 days ago", which reads as "since 12 days
 * ago" once a lane prefixes it - a sentence with two tenses in it.
 *
 * `dueDate.ts` settled that user-facing ages are counted in calendar days, and `health_since`
 * is a DATE for exactly that reason: the day the colour started is a day, not a moment, and
 * counting it in hours would make "since 1 day" appear at different times for two readers in
 * two timezones looking at the same customer.
 */
export function sinceLabel(value: FieldValue | undefined): string | null {
  if (typeof value !== 'string') return null;
  const [year, month, date] = value.slice(0, 10).split('-').map(Number);
  if (year === undefined || month === undefined || date === undefined) return null;
  const [nowYear, nowMonth, nowDate] = todayKey().split('-').map(Number);
  if (nowYear === undefined || nowMonth === undefined || nowDate === undefined) return null;
  const days = Math.round(
    (new Date(nowYear, nowMonth - 1, nowDate).getTime() -
      new Date(year, month - 1, date).getTime()) /
      86_400_000,
  );
  if (days < 0) return null;
  if (days === 0) return 'since today';
  if (days === 1) return 'since yesterday';
  return `for ${String(days)} days`;
}

/**
 * WHICH GROUP IS THE COMPUTED BLOCK, decided by the CONFIG rather than by a name (INV-7).
 *
 * A group is the health block when it holds the colour field the rules write. Matching on
 * the label "Health" would break the day somebody renames the section, which is a thing this
 * product lets them do for free - and matching on all five would break the day somebody
 * moves one of them somewhere else, which would silently turn the block back into rows.
 */
export function isHealthGroup(
  config: HealthConfig | undefined,
  groupFields: readonly FieldMetadata[],
): boolean {
  if (config === undefined) return false;
  return groupFields.some((field) => field.id === config.colorField);
}

/** The five the block draws itself, so the section can render whatever else is in the group. */
export function computedHealthFieldIds(config: HealthConfig): ReadonlySet<string> {
  return new Set([
    config.colorField,
    config.reasonField,
    config.sinceField,
    config.computedAtField,
    config.needsAssessmentField,
  ]);
}
